import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "../context/I18nContext";
import { setPasswordWithResetToken } from "../services/auth";
import "./Auth.css";

function readSessionValue(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function clearSessionValues() {
  try {
    sessionStorage.removeItem("casaclass:passwordResetEmail");
    sessionStorage.removeItem("casaclass:passwordResetToken");
  } catch (e) {
    // Ignore storage errors.
  }
}

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const initial = useMemo(() => {
    const state = location.state;
    const emailFromState = state?.email;
    const tokenFromState = state?.token;

    const emailFromSession = readSessionValue("casaclass:passwordResetEmail");
    const tokenFromSession = readSessionValue("casaclass:passwordResetToken");

    return {
      email: emailFromState || emailFromSession || "",
      token: tokenFromState || tokenFromSession || "",
    };
  }, [location.state]);

  const [email] = useState(initial.email);
  const [token] = useState(initial.token);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email || !token) {
      navigate("/forgot-password", { replace: true });
    }
  }, [email, token, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setStatus("");

    if (!password) {
      setError(
        t("authPage.passwordRequired", {
          defaultValue: "New password is required.",
        }),
      );
      return;
    }
    if (password !== confirm) {
      setError(
        t("authPage.passwordMismatch", {
          defaultValue: "Passwords do not match.",
        }),
      );
      return;
    }

    setLoading(true);
    try {
      await setPasswordWithResetToken({ email, token, password });
      clearSessionValues();
      navigate("/login", {
        replace: true,
        state: { passwordResetSuccess: true },
      });
    } catch (err) {
      setError(err.message || t("authPage.loginError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="auth-shell">
      <div className="auth-card">
        <h1>{t("authPage.resetTitle", { defaultValue: "Reset password" })}</h1>
        <p>
          {t("authPage.resetLead", {
            defaultValue: "Choose a new password for your account.",
          })}
        </p>

        {status && <div className="auth-success">{status}</div>}
        {error && <div className="auth-error">{error}</div>}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-field">
            <label htmlFor="email">{t("authPage.email")}</label>
            <input
              id="email"
              name="email"
              type="email"
              value={email}
              disabled
            />
          </div>

          <div className="auth-field">
            <label htmlFor="password">
              {t("authPage.newPassword", { defaultValue: "New password" })}
            </label>
            <div className="password-wrap">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={
                  showPassword
                    ? t("authPage.hidePassword")
                    : t("authPage.showPassword")
                }
                title={
                  showPassword
                    ? t("authPage.hidePassword")
                    : t("authPage.showPassword")
                }
              >
                <i
                  className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="confirm">
              {t("authPage.confirmPassword", {
                defaultValue: "Confirm password",
              })}
            </label>
            <div className="password-wrap">
              <input
                id="confirm"
                name="confirm"
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword((current) => !current)}
                aria-label={
                  showConfirmPassword
                    ? t("authPage.hidePassword")
                    : t("authPage.showPassword")
                }
                title={
                  showConfirmPassword
                    ? t("authPage.hidePassword")
                    : t("authPage.showPassword")
                }
              >
                <i
                  className={`fa-solid ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          <div className="auth-actions">
            <button type="submit" className="btn primary" disabled={loading}>
              {loading
                ? t("authPage.forgotSending", { defaultValue: "Saving..." })
                : t("authPage.resetCta", { defaultValue: "Reset password" })}
            </button>
            <div className="auth-links">
              <Link to="/login">{t("authPage.forgotBack")}</Link>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
